import React from 'react';
import { Box, Typography, TablePagination, CircularProgress, Snackbar, InputAdornment } from '@material-ui/core';
import { TextField, SnackBar } from '@orijinworks/frontend-commons';
import SearchIcon from '@material-ui/icons/Search';
import { SEARCH_MIN_LENGTH } from '../../../constants/person-constants';
import { usePersonsManagerHook } from './hooks/usePersonsManagerHook';
import { PersonCard } from './components/PersonCard';
import { PersonDetailView } from './components/PersonDetailView';

const PersonsManager = () => {
  const {
    classes,
    searchInput,
    handleSearchChange,
    handleSearchPaste,
    debouncedSearch,
    showHint,
    searchLoading,
    searchError,
    results,
    totalCount,
    muiPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    attributesByPersonId,
    servicesByPersonId,
    selectedPerson,
    setSelectedPersonId,
    handleAssociationsSaved,
    toast,
    handleCloseToast,
  } = usePersonsManagerHook();

  return (
    <Box className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        Person Configuration
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Search by name, username, doc ID, or email. Open a person card for full attribute and service management.
      </Typography>

      <Box className={classes.searchRow}>
        <TextField
          fullWidth
          placeholder={`Search (min. ${String(SEARCH_MIN_LENGTH)} characters)`}
          value={searchInput}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={classes.searchIcon} />
              </InputAdornment>
            ),
          }}
          inputProps={{
            'aria-label': 'Search persons',
            onInput: handleSearchChange,
            onPaste: handleSearchPaste,
          }}
        />
      </Box>

      {showHint && (
        <Typography variant="caption" className={classes.empty}>
          Enter at least {SEARCH_MIN_LENGTH} characters (typing or paste) to search.
        </Typography>
      )}

      {searchLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!searchLoading && searchError && debouncedSearch.trim().length >= SEARCH_MIN_LENGTH && (
        <Typography color="error" className={classes.empty}>
          Search could not complete.
        </Typography>
      )}

      {!searchLoading &&
        !searchError &&
        debouncedSearch.trim().length >= SEARCH_MIN_LENGTH &&
        results.length === 0 && (
        <Typography className={classes.empty}>No matching persons.</Typography>
      )}

      {!searchLoading && results.length > 0 && (
        selectedPerson ? (
          <PersonDetailView
            person={selectedPerson}
            attributesState={attributesByPersonId[selectedPerson.personId]}
            servicesState={servicesByPersonId[selectedPerson.personId]}
            onBack={() => setSelectedPersonId(null)}
            onSaved={() => handleAssociationsSaved(selectedPerson.personId)}
          />
        ) : (
          <>
            <Box className={classes.grid}>
              {results.map((person) => (
                <PersonCard
                  key={person.personId}
                  person={person}
                  attributesState={attributesByPersonId[person.personId]}
                  servicesState={servicesByPersonId[person.personId]}
                  onOpenDetail={setSelectedPersonId}
                />
              ))}
            </Box>
            <TablePagination
              className={classes.pagination}
              component="div"
              count={totalCount}
              page={muiPage}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )
      )}

      {toast && (
        <Snackbar
          open
          autoHideDuration={2800}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <SnackBar
            isShown
            type={toast.type === 'error' || toast.type === 'success' ? toast.type : 'info'}
            onClose={handleCloseToast}
          >
            {toast.message}
          </SnackBar>
        </Snackbar>
      )}
    </Box>
  );
};

export default PersonsManager;
